/**
 * Onboarding — First-time user experience overlay.
 *
 * 4-step swipeable flow (culturally neutral, WW positioning):
 * 1. Welcome: AWENI logo animation + tagline
 * 2. Choose Modules: toggle grid of available modules
 * 3. Set Language: 5 language buttons with flags
 * 4. Done: confetti animation + "Let's go!" button
 *
 * Shown once, state saved to localStorage (aweni_onboarded).
 * Can be skipped at any step.
 * Prayer times opt-in happens in-context (Home widget + Memoriz first open),
 * never during the intro — we don't ask culture-specific questions to fresh users.
 */

import type { AweniContext } from '../context/AweniContext';

const STORAGE_KEY = 'aweni_onboarded';

/** Check whether onboarding has been completed. */
export function isOnboarded(): boolean {
  return localStorage.getItem(STORAGE_KEY) === '1';
}

/** Mark onboarding as complete. */
function completeOnboarding(): void {
  localStorage.setItem(STORAGE_KEY, '1');
}

/** Available modules for the module picker step. */
const MODULE_OPTIONS = [
  { id: 'games', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="6" width="20" height="12" rx="2"/><line x1="6" y1="12" x2="10" y2="12"/><line x1="8" y1="10" x2="8" y2="14"/><circle cx="15" cy="10" r="1"/><circle cx="18" cy="13" r="1"/></svg>', color: '#3B82F6' },
  { id: 'memoriz', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a7 7 0 0 0-7 7c0 5 7 13 7 13s7-8 7-13a7 7 0 0 0-7-7z"/></svg>', color: '#0A0A0A' },
  { id: 'balancetab', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M2 10h20"/></svg>', color: '#8B5CF6' },
  { id: 'blog', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>', color: '#14B8A6' },
  { id: 'selecto', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"/></svg>', color: '#F97316' },
  { id: 'chat', icon: '<svg class="w-8 h-8" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>', color: '#EC4899' },
];

/** Language options with flag emoji. */
const LANGUAGES = [
  { code: 'en', flag: '\uD83C\uDDEC\uD83C\uDDE7', label: 'English' },
  { code: 'fr', flag: '\uD83C\uDDEB\uD83C\uDDF7', label: 'Francais' },
  { code: 'ar', flag: '\uD83C\uDDF8\uD83C\uDDE6', label: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629' },
  { code: 'es', flag: '\uD83C\uDDEA\uD83C\uDDF8', label: 'Espanol' },
  { code: 'de', flag: '\uD83C\uDDE9\uD83C\uDDEA', label: 'Deutsch' },
];

/**
 * Mount the onboarding overlay if the user has not yet completed it.
 * Returns a Promise that resolves when onboarding is dismissed.
 */
export function mountOnboarding(root: HTMLElement, ctx: AweniContext): Promise<void> {
  if (isOnboarded()) return Promise.resolve();

  return new Promise<void>((resolve) => {
    const t = ctx.i18n.t.bind(ctx.i18n);
    const locale = ctx.i18n.locale || 'en';
    let currentStep = 0;
    const totalSteps = 4;
    const enabledModules = new Set(MODULE_OPTIONS.map((m) => m.id));
    let selectedLang = locale;

    // Inject animation styles
    if (!document.getElementById('onboarding-styles')) {
      const style = document.createElement('style');
      style.id = 'onboarding-styles';
      style.textContent = `
        @keyframes obLogoReveal {
          0% { opacity: 0; transform: scale(0.5) rotate(-10deg); }
          60% { opacity: 1; transform: scale(1.1) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes obFadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes obConfetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(300px) rotate(720deg); opacity: 0; }
        }
        @keyframes obSlideIn {
          from { opacity: 0; transform: translateX(30px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes obSlideOut {
          from { opacity: 1; transform: translateX(0); }
          to { opacity: 0; transform: translateX(-30px); }
        }
        .ob-overlay {
          position: fixed;
          inset: 0;
          z-index: 9999;
          background: var(--bg-primary);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        }
        .ob-page {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 24px;
          text-align: center;
          animation: obSlideIn var(--motion-duration-slow) var(--motion-easing-enter) forwards;
        }
        .ob-page.exiting {
          animation: obSlideOut var(--motion-duration-slow) var(--motion-easing-exit) forwards;
        }
        .ob-dots {
          display: flex;
          gap: 8px;
          justify-content: center;
          padding: 16px 0;
        }
        .ob-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: var(--border);
          transition: background var(--motion-duration-base), width var(--motion-duration-base);
        }
        .ob-dot.active {
          background: var(--accent);
          width: 24px;
          border-radius: 4px;
        }
        .ob-skip {
          position: absolute;
          top: 16px;
          right: 16px;
          z-index: 10;
          font-size: 14px;
          font-weight: 600;
          color: var(--text-secondary);
          padding: 8px 16px;
          border-radius: 8px;
          background: transparent;
          border: none;
          cursor: pointer;
          min-height: 44px;
          min-width: 44px;
        }
        [dir="rtl"] .ob-skip {
          right: auto;
          left: 16px;
        }
        .ob-module-toggle {
          min-height: 44px;
          min-width: 44px;
          cursor: pointer;
          transition: transform var(--motion-duration-fast), box-shadow var(--motion-duration-fast);
        }
        .ob-module-toggle:active { transform: scale(0.95); }
        .ob-module-toggle.active { box-shadow: 0 0 0 3px var(--accent); }
        .ob-lang-btn {
          min-height: 44px;
          min-width: 44px;
          cursor: pointer;
          transition: transform var(--motion-duration-fast), box-shadow var(--motion-duration-fast);
        }
        .ob-lang-btn:active { transform: scale(0.95); }
        .ob-lang-btn.active { box-shadow: 0 0 0 3px var(--accent); background: var(--accent) !important; color: var(--accent-text) !important; }
        .ob-confetti-piece {
          position: absolute;
          width: 10px;
          height: 10px;
          border-radius: 2px;
          animation: obConfetti var(--motion-duration-deliberate) var(--motion-easing-exit) forwards;
          pointer-events: none;
        }
      `;
      document.head.appendChild(style);
    }

    // Create overlay
    const overlay = document.createElement('div');
    overlay.className = 'ob-overlay';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', t('onboarding_welcome'));
    const isRtl = ctx.i18n.dir === 'rtl';
    overlay.setAttribute('dir', isRtl ? 'rtl' : 'ltr');

    // Skip button
    const skipBtn = document.createElement('button');
    skipBtn.className = 'ob-skip';
    skipBtn.textContent = t('onboarding_skip');
    skipBtn.setAttribute('aria-label', t('onboarding_skip'));
    skipBtn.addEventListener('click', finish);
    overlay.appendChild(skipBtn);

    // Page container
    const pageContainer = document.createElement('div');
    pageContainer.style.cssText = 'flex:1;display:flex;flex-direction:column;overflow:hidden;';
    overlay.appendChild(pageContainer);

    // Dots
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'ob-dots';
    dotsContainer.setAttribute('role', 'tablist');
    dotsContainer.setAttribute('aria-label', 'Onboarding steps');
    for (let i = 0; i < totalSteps; i++) {
      const dot = document.createElement('div');
      dot.className = `ob-dot${i === 0 ? ' active' : ''}`;
      dot.setAttribute('role', 'tab');
      dot.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      dotsContainer.appendChild(dot);
    }
    overlay.appendChild(dotsContainer);

    // Navigation area
    const navArea = document.createElement('div');
    navArea.style.cssText = 'padding:16px 24px 32px;display:flex;justify-content:center;';
    const nextBtn = document.createElement('button');
    nextBtn.style.cssText = `
      width: 100%;
      max-width: 320px;
      padding: 14px 32px;
      border-radius: 14px;
      background: var(--accent);
      color: #fff;
      font-size: 16px;
      font-weight: 700;
      border: none;
      cursor: pointer;
      min-height: 48px;
      transition: transform var(--motion-duration-fast), opacity var(--motion-duration-fast);
    `;
    nextBtn.addEventListener('click', nextStep);
    nextBtn.addEventListener('mousedown', () => { nextBtn.style.transform = 'scale(0.97)'; });
    nextBtn.addEventListener('mouseup', () => { nextBtn.style.transform = 'scale(1)'; });
    navArea.appendChild(nextBtn);
    overlay.appendChild(navArea);

    // Swipe support
    let touchStartX = 0;
    overlay.addEventListener('touchstart', (e) => { touchStartX = e.touches[0].clientX; }, { passive: true });
    overlay.addEventListener('touchend', (e) => {
      const diff = e.changedTouches[0].clientX - touchStartX;
      if (Math.abs(diff) > 60) {
        if (diff < 0) nextStep(); // swipe left = next
        else if (currentStep > 0) { currentStep -= 2; nextStep(); } // swipe right = prev
      }
    }, { passive: true });

    // Keyboard: Escape to skip, Arrow keys to navigate
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') finish();
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') nextStep();
      if ((e.key === 'ArrowLeft' || e.key === 'ArrowUp') && currentStep > 0) { currentStep -= 2; nextStep(); }
    };
    document.addEventListener('keydown', keyHandler);

    root.appendChild(overlay);
    renderStep();

    function renderStep(): void {
      // Animate out old page
      const oldPage = pageContainer.querySelector('.ob-page') as HTMLElement | null;
      if (oldPage) {
        oldPage.classList.add('exiting');
        setTimeout(() => oldPage.remove(), 300);
      }

      setTimeout(() => {
        const page = document.createElement('div');
        page.className = 'ob-page';
        page.setAttribute('role', 'tabpanel');

        switch (currentStep) {
          case 0: renderWelcome(page); break;
          case 1: renderModules(page); break;
          case 2: renderLanguage(page); break;
          case 3: renderDone(page); break;
        }

        pageContainer.appendChild(page);

        // Update dots
        const dots = dotsContainer.querySelectorAll('.ob-dot');
        dots.forEach((d, i) => {
          d.className = `ob-dot${i === currentStep ? ' active' : ''}`;
          d.setAttribute('aria-selected', i === currentStep ? 'true' : 'false');
        });

        // Update next button text
        if (currentStep === totalSteps - 1) {
          nextBtn.textContent = t('onboarding_lets_go');
          skipBtn.style.display = 'none';
        } else {
          nextBtn.textContent = t('common_next') || 'Next';
          skipBtn.style.display = '';
        }
      }, oldPage ? 300 : 0);
    }

    function renderWelcome(page: HTMLElement): void {
      // Brand v1: real monogram on cream tile, no fake "A" letter placeholder.
      page.innerHTML = `
        <div style="animation:obLogoReveal var(--motion-duration-deliberate) var(--motion-easing-enter) forwards;margin-bottom:24px;">
          <div style="width:120px;height:120px;border-radius:28px;background:#FAF7F2;display:flex;align-items:center;justify-content:center;margin:0 auto;box-shadow:0 12px 40px rgba(10,10,10,0.18);">
            <img src="/aweni/icons/pwa-192x192.png" alt="AWENI" width="96" height="96" style="display:block;" />
          </div>
        </div>
        <h1 style="font-size:28px;font-weight:800;color:var(--text-primary);margin-bottom:8px;animation:obFadeUp var(--motion-duration-deliberate) var(--motion-easing-enter) var(--motion-duration-base) both;">
          ${t('onboarding_welcome')}
        </h1>
        <p style="font-size:16px;color:var(--text-secondary);max-width:280px;line-height:1.5;animation:obFadeUp var(--motion-duration-deliberate) var(--motion-easing-enter) var(--motion-duration-slow) both;">
          ${t('onboarding_welcome_sub')}
        </p>
      `;
    }

    function renderModules(page: HTMLElement): void {
      const title = document.createElement('h2');
      title.style.cssText = 'font-size:22px;font-weight:700;color:var(--text-primary);margin-bottom:8px;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) both;';
      title.textContent = t('onboarding_modules');

      const subtitle = document.createElement('p');
      subtitle.style.cssText = 'font-size:14px;color:var(--text-secondary);margin-bottom:24px;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-fast) both;';
      subtitle.textContent = t('onboarding_modules_sub');

      const grid = document.createElement('div');
      grid.style.cssText = 'display:grid;grid-template-columns:repeat(3,1fr);gap:12px;max-width:320px;width:100%;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-base) both;';

      for (const mod of MODULE_OPTIONS) {
        const card = document.createElement('button');
        card.className = `ob-module-toggle${enabledModules.has(mod.id) ? ' active' : ''}`;
        card.setAttribute('role', 'switch');
        card.setAttribute('aria-checked', enabledModules.has(mod.id) ? 'true' : 'false');
        card.setAttribute('aria-label', t(`nav_${mod.id}`) || mod.id);
        card.style.cssText = `
          display:flex;flex-direction:column;align-items:center;gap:8px;padding:16px 8px;
          border-radius:16px;border:2px solid ${enabledModules.has(mod.id) ? 'var(--accent)' : 'var(--border)'};
          background:var(--card-bg);
        `;
        card.innerHTML = `
          <div style="width:48px;height:48px;border-radius:12px;display:flex;align-items:center;justify-content:center;background:${mod.color}15;color:${mod.color};">
            ${mod.icon}
          </div>
          <span style="font-size:12px;font-weight:600;color:var(--text-primary);">${t(`nav_${mod.id}`) || mod.id}</span>
        `;

        card.addEventListener('click', () => {
          if (enabledModules.has(mod.id)) {
            enabledModules.delete(mod.id);
            card.classList.remove('active');
            card.style.borderColor = 'var(--border)';
            card.setAttribute('aria-checked', 'false');
          } else {
            enabledModules.add(mod.id);
            card.classList.add('active');
            card.style.borderColor = 'var(--accent)';
            card.setAttribute('aria-checked', 'true');
          }
        });

        grid.appendChild(card);
      }

      page.append(title, subtitle, grid);
    }

    function renderLanguage(page: HTMLElement): void {
      const title = document.createElement('h2');
      title.style.cssText = 'font-size:22px;font-weight:700;color:var(--text-primary);margin-bottom:8px;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) both;';
      title.textContent = t('onboarding_language');

      const subtitle = document.createElement('p');
      subtitle.style.cssText = 'font-size:14px;color:var(--text-secondary);margin-bottom:24px;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-fast) both;';
      subtitle.textContent = t('onboarding_language_sub');

      const list = document.createElement('div');
      list.style.cssText = 'display:flex;flex-direction:column;gap:10px;max-width:320px;width:100%;animation:obFadeUp var(--motion-duration-slow) var(--motion-easing-enter) var(--motion-duration-base) both;';

      for (const lang of LANGUAGES) {
        const btn = document.createElement('button');
        btn.className = `ob-lang-btn${selectedLang === lang.code ? ' active' : ''}`;
        btn.setAttribute('role', 'radio');
        btn.setAttribute('aria-checked', selectedLang === lang.code ? 'true' : 'false');
        btn.setAttribute('aria-label', lang.label);
        btn.style.cssText = `
          display:flex;align-items:center;gap:12px;padding:14px 20px;
          border-radius:14px;border:2px solid ${selectedLang === lang.code ? 'var(--accent)' : 'var(--border)'};
          background:${selectedLang === lang.code ? 'var(--accent)' : 'var(--card-bg)'};
          color:${selectedLang === lang.code ? '#fff' : 'var(--text-primary)'};
          font-size:16px;font-weight:600;
          width:100%;text-align:start;
        `;
        btn.innerHTML = `
          <span style="font-size:28px;">${lang.flag}</span>
          <span>${lang.label}</span>
        `;
        btn.addEventListener('click', () => {
          selectedLang = lang.code;
          ctx.i18n.setLocale(lang.code);
          // Re-render to update all labels
          list.querySelectorAll('.ob-lang-btn').forEach((b) => {
            const bel = b as HTMLButtonElement;
            const code = LANGUAGES.find((l) => bel.textContent?.includes(l.label))?.code;
            const isActive = code === lang.code;
            bel.className = `ob-lang-btn${isActive ? ' active' : ''}`;
            bel.style.borderColor = isActive ? 'var(--accent)' : 'var(--border)';
            bel.style.background = isActive ? 'var(--accent)' : 'var(--card-bg)';
            bel.style.color = isActive ? '#fff' : 'var(--text-primary)';
            bel.setAttribute('aria-checked', isActive ? 'true' : 'false');
          });
        });
        list.appendChild(btn);
      }

      page.append(title, subtitle, list);
    }

    function renderDone(page: HTMLElement): void {
      page.innerHTML = `
        <div style="animation:obFadeUp var(--motion-duration-deliberate) var(--motion-easing-enter) both;position:relative;">
          <div style="width:96px;height:96px;border-radius:50%;background:var(--accent);display:flex;align-items:center;justify-content:center;margin:0 auto 24px;box-shadow:0 12px 40px rgba(10, 10, 10,0.3);">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="3" stroke-linecap="round"><polyline points="20 6 9 17 4 12"/></svg>
          </div>
          <h2 style="font-size:28px;font-weight:800;color:var(--text-primary);margin-bottom:8px;">
            ${t('onboarding_done')}
          </h2>
          <p style="font-size:16px;color:var(--text-secondary);max-width:280px;margin:0 auto;line-height:1.5;">
            ${t('onboarding_done_sub')}
          </p>
        </div>
      `;
      // Spawn confetti
      spawnConfetti(page);
    }

    function spawnConfetti(container: HTMLElement): void {
      const colors = ['#0A0A0A', '#3B82F6', '#F59E0B', '#8B5CF6', '#EC4899', '#14B8A6'];
      for (let i = 0; i < 40; i++) {
        const piece = document.createElement('div');
        piece.className = 'ob-confetti-piece';
        piece.style.cssText += `
          left: ${Math.random() * 100}%;
          top: ${-10 - Math.random() * 20}%;
          background: ${colors[Math.floor(Math.random() * colors.length)]};
          animation-delay: ${Math.random() * 800}ms;
          animation-duration: ${1500 + Math.random() * 1500}ms;
          width: ${6 + Math.random() * 8}px;
          height: ${6 + Math.random() * 8}px;
          border-radius: ${Math.random() > 0.5 ? '50%' : '2px'};
        `;
        container.appendChild(piece);
      }
    }

    function nextStep(): void {
      if (currentStep >= totalSteps - 1) {
        finish();
        return;
      }
      currentStep++;
      renderStep();
    }

    function finish(): void {
      completeOnboarding();
      // Save module preferences
      localStorage.setItem('aweni_enabled_modules', JSON.stringify(Array.from(enabledModules)));
      // Animate out
      overlay.style.transition = 'opacity var(--motion-duration-slow) var(--motion-easing-default)';
      overlay.style.opacity = '0';
      setTimeout(() => {
        overlay.remove();
        document.removeEventListener('keydown', keyHandler);
        resolve();
      }, 400);
    }
  });
}
